import {ChangeDetectorRef, Component, NgZone, OnInit, ViewChild} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {Observable, Observer, Subscription, timer} from "rxjs";
import "rxjs/add/observable/forkJoin";
import {ContentViewComponent} from "../content/content-view.component";
import {EventBus} from "../event/EventBus";
import {ACTIVITY_STATUS, ActivityEvent, ActivityEventData, ActivityModalEvent} from "../event/ActivityEvent";
import {ContentCssClass, CssSelection, CssSentence, CssWord} from "../content/content-view.objects";
import {AllowedSelectionType, ContentCursor} from "../services/content/content.cursor";
import {ContentService} from "../services/content/content.service";
import {ClientConfiguration} from "../services/configuration/client.config";
import {ReadToMeService} from "../services/reading/readtome.service";
import {RecordingService} from "../services/recording/recording.service";
import {GlossaryService} from "../services/glossary/glossary.service";
import {WordWallRecordingService} from "../services/recording/wordwall.recording.service";
import {TimerStrategy} from "../services/configuration/timer.config";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {MICROPHONECHECK_STATUS, MicrophoneCheckEvent} from "../event/MicrophoneCheckEvent";
import {QuestionService} from "../services/question/question.service";
import {IQuestionEntry} from "../api/data-model";
import {StepActivity, StepService} from "../services/step/step.service";
import {WordWallCompleteEvent, WordWallLaunchEvent} from "../event/WordWallEvents";
import {
    PreviewStepEvent,
    QuizStepEvent,
    ReadAndRecordStepEvent,
    StepStatusEvent,
    WordWallStepEvent
} from "../event/StepEvents";
import {
    BookIntroEndEvent,
    BookIntroLaunchEvent,
    PleaseWaitEvent,
    PleaseWaitTarget,
    ResetSelectionLoadedEvent,
    SelectionLoadedEvent
} from "../event/ContentEvents";
import {UserModelService} from "../services/usermodel/usermodel.service";
import {SessionService} from "../services/configuration/session.service";
import {ContentFormatter} from "../content/content.formatter";
import {
    CountdownStartEvent,
    CountdownStopEvent,
    NextSentenceEvent,
    PauseSelectionEvent,
    ReadMyRecordingEvent,
    ReadSelectionEvent,
    RecordSelectionEvent
} from "../event/ReadAndRecordEvents";
import {UserModelChangedEvent, UserModelInitializedEvent} from "../event/UserModelEvents";
import {TAI_STATUS, TaiEvent} from "../event/TaiEvent";
import {ReadMyRecordingService} from "../services/reading/readmyrecording.service";
import {ComponentBase} from "../base/ComponentBase";
import {RaEvent} from "../event/RaEvent";
import {PreviewService} from "../services/reading/preview.service";
import {QuizService} from "../services/quiz/quiz.service";
import {PointService} from "../services/points/points.service";
import {ScrollService} from "../services/scroll/scroll.service";
import {PIPELINE_API_STATUS, PipelineSaveEvent} from "../pipeline/PipelineSaveEvent";
import {SelectionRecordingStatus} from "../services/recording/selection-recording-status";
import {ModalConfig} from "../services/notification/modal.config";
import {AssignmentService, SelectionsAndCategories} from "../services/assignment/assignment.service";
import {StepStateUpdater} from "../services/step/step-state";
import {GAEventService} from "../services/analytics/GAEventServce";
import {PreviewStep} from "../services/step/step.objects";
import {DisableExitButtonEvent, EnableExitButtonEvent} from "../event/ExitButtonEvents";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {BookSessionType, ErrorType, Scilearn} from "../constants";
import {NetworkService} from "../services/interaction/network.service";
import {filter, map, take} from "rxjs/operators";
import {first} from "rxjs/internal/operators/first";
import {fromEvent} from "rxjs/internal/observable/fromEvent";
import {SESSION_STATUS, SessionEvent} from "../event/SessionEvent";
// @ts-ignore
import imagesLoaded from "imagesloaded";


export interface IReadingMode {
    // Called when word must be played back
    startWord(wordItem: CssWord): void;
    // Called when sentence must be played back or recorded
    startSentence(sentenceItem: CssSentence, callback: Function): void;
    // Call each time entering mode
    activate(isPipeline: boolean): void;
    // Call each time leaving mode
    pause(): void;
    // Callback when startSentence completes
    sentenceFinishedCallback: Function;
    // Flag to modify Reading UI when mode becomes active
    changeUIWhenActive: boolean;
    // granularity of allowed user selection (WORD, SENTENCE, TEXT, NONE)
    allowedSelectionType: AllowedSelectionType;
}

@Component({
               selector: "reading",
               templateUrl: "./reading.component.html",
               styleUrls: ["./reading.component.scss"]
           })
export class ReadingComponent extends ComponentBase implements OnInit {

    public sidePosFixed = true;
    private _currentReadingMode: IReadingMode;
    private _selectionName: string;
    public _selection: CssSelection = new CssSelection();

    private _sentenceDelayTimer: number;
    private _sentenceDelayTime: number = 1000; // TODO: pull in through settings object
    private _sentenceCount: number = 0;
    private currentlyPlayingSentence:CssSentence;

    public showGlossary = false;
    public showTai = false;
    public showCountdown = false;
    public countDownDisplaying = false;
    public showWordColorLegend = false;
    public currentTaiQuestion: IQuestionEntry;
    public contentViewStyles = {};

    public isWordWallActiveClass: boolean = true;
    public isBookIntroVisible: boolean = false;
    public isPreviewActiveClass: boolean = false;
    public isReadAndRecordActiveClass: boolean = false;
    public isQuizActiveClass: boolean = false;
    public scrollArrow: boolean = false;
    public isPipeline:boolean = false;
    public isInitialized:boolean = false;

    public playPreviewInstruction: boolean = true;
    public playFluencyInstruction: boolean = true;
    public playQuizInstruction: boolean = true;

    private timingEvent: Map<string, number> = new Map<string, number>();

    @ViewChild(ContentViewComponent) contentView: ContentViewComponent;

    constructor(private previewService: PreviewService,
                private quizService: QuizService,
                private readToMeService: ReadToMeService,
                private recordingService: RecordingService,
                private readMyRecordingService: ReadMyRecordingService,
                private contentService: ContentService,
                private activatedRoute: ActivatedRoute,
                private _ngZone: NgZone,
                private changeDetectorRef: ChangeDetectorRef,
                private eventBus: EventBus,
                private config: ClientConfiguration,
                private questionService: QuestionService,
                private wordWallRecordingService: WordWallRecordingService,
                private log: BugsnagLoggingService,
                private contentCursor: ContentCursor,
                private userModelService: UserModelService,
                private audioQueueService: AudioQueueService,
                private stepService: StepService,
                private pointService: PointService,
                private scrollService: ScrollService,
                private sessionService: SessionService,
                private selectionRecordingStatus: SelectionRecordingStatus,
                private modalConfig: ModalConfig,
                private assignmentService: AssignmentService,
                private stepStateUpdater: StepStateUpdater,
                private gaEventService: GAEventService,
                private networkService: NetworkService,
                public glossaryService: GlossaryService
    ) {
        super("ReadingComponent");

        // TODO: should go to an instructional audio class to handle language
        this.reactToStepSwitching();
    }

    ngOnInit(): void {
        super.ngOnInit();

        // tslint:disable-next-line:no-string-literal
        let selectionRoute = this.activatedRoute.params.pipe(map(params => params["selectionName"]));
        if (this.config.isSuperUserMode) {
            this.isPipeline = true;
        }
        // tslint:disable-next-line:no-string-literal
        this.activatedRoute.queryParams.pipe(map(queryParams => queryParams["isPipeline"])).subscribe((param) => {
            if (param) {
                this.isPipeline = true;
            }
        });

        this.subscriptions.push(selectionRoute.subscribe(selectionName => {
            // if we can't initialize, probably the page reloaded. Need to just exit at this point, because
            // user won't be able to record (recognizer won't be initialized)
            if (!this.assignmentService.canInitialize()) {
                this.eventBus.pipe(filter((event: SessionEvent) => event.type === SessionEvent.TYPE
                    && event.status === SESSION_STATUS.start_trainingsession))
                    .subscribe((event) => {
                        this.modalConfig.openMessageModal("selection_load_error");
                    });
            }
            else {
                if (this.assignmentService.isInitialized()) {
                    this.initSelection(selectionName);
                }
                else {
                    this.assignmentService.retrieveAssignments().subscribe((response: SelectionsAndCategories) => {
                        this.initSelection(selectionName);
                    }, () => {
                        this.modalConfig.openMessageModal("selection_load_error");
                    });
                }
            }
        }));
        this.recordingService.changeDetectorRef = this.changeDetectorRef;
        this.scrollService.changeDetectorRef = this.changeDetectorRef;
    }

    private initSelection(selectionName: string): void {
        this._selectionName = selectionName;
        if (!this._selectionName) {
            throw new Error("No selection name");
        }

        this.gaEventService.emitEvent("selection", "selection name", selectionName, 1);
        this.contentCursor.clearSelection();
        this.readToMeService.sentenceFinishedCallback = this.readingSentenceFinished;
        this.readMyRecordingService.sentenceFinishedCallback = this.readingSentenceFinished;
        this.recordingService.sentenceFinishedCallback = this.recordSentenceFinished;

        // TODO: need a better way to override this if pipeline (super user)
        let sub = this.contentCursor.wordChanged.subscribe((word: CssWord) => {
            if (this._currentReadingMode && !this.isPipeline) {
                this._currentReadingMode.startWord(word);
            }
        });
        this.subscriptions.push(sub);

        let sentenceSub = this.contentCursor.sentenceChanged.subscribe(sentence => {
            if ((this.stepService.isReading || this.stepService.isReadingMyRecording)
                && sentence.userSelected && !this.scrollService.isCurrentScrolling()) {
                this.audioQueueService.reset().subscribe(() => {
                    this.currentlyPlayingSentence = sentence;
                    this.initiateScrollProcess();
                });
            }
        });

        this.subscriptions.push(sentenceSub);

        sub = this.eventBus.subscribe((event: RaEvent) => {

            switch (event.type) {

                case ActivityEvent.TYPE:
                    let isGlossaryActivity = (<ActivityEvent>event).data.type === "glossary";
                    let isGlossaryStart = (<ActivityEvent>event).status === ACTIVITY_STATUS.activity_start;
                    if ((isGlossaryActivity && isGlossaryStart)) {
                        console.log("reading component: got glossary start event");
                        this.glossaryService.setGlossaryOpened(true);
                    }
                    break;

                case BookIntroLaunchEvent.TYPE:
                    this.isWordWallActiveClass = false;
                    this.isBookIntroVisible = true;
                    break;

                case BookIntroEndEvent.TYPE:
                    this.isWordWallActiveClass = true;
                    this.isBookIntroVisible = false;
                    break;

                case UserModelInitializedEvent.TYPE:
                    this.stepStateUpdater.determineCurrentActivity(this._selection);
                    this.setWordColorLegendVisibility();
                    console.log("user model initialized ", this.userModelService);
                    break;

                case UserModelChangedEvent.TYPE:
                    this.stepStateUpdater.determineCurrentStep(this._selection, false);
                    this.setWordColorLegendVisibility();
                    let userModelChangedEvent = <UserModelChangedEvent>event;
                    if (userModelChangedEvent.delta.justCompletedSentence && !this.isRecording()) {
                        let cssClass;
                        if (this.userModelService.canPlayback) {
                            cssClass = ContentCssClass.MODE_RECORDING_DONE;
                            this.log.log("calling format colors from readingComponent: selection = " +
                                this.contentCursor.selection.referenceStory());
                            ContentFormatter.formatSelectionFromRecordReadingStates(
                                this.contentCursor.selection,
                                this.userModelService.recordReadingStates);
                        } else {
                            cssClass = ContentCssClass.MODE_RECORDING_PAUSED;
                            this.log.log("calling format read/unread from readingComponent: selection = " +
                                this.contentCursor.selection.referenceStory() + " currentSentence="
                                + this.userModelService.currentSentenceId);
                            ContentFormatter.formatSelectionFromCurrentRecordingSentence(
                                this.contentCursor.selection,
                                this.userModelService.currentSentenceId);
                        }
                        this.contentCursor.setMode(cssClass);
                    }
                    console.log("user model changed ", this.userModelService);
                    break;

                case WordWallLaunchEvent.TYPE:
                    this.wordWallStep();
                    let launchEvent = <WordWallLaunchEvent>event;
                    this.wordWallRecordingService.activate(launchEvent.isGuided);
                    this.gaEventService.startDurationEvent("wordwall-duration", "wordwall", "start");
                    break;

                case WordWallCompleteEvent.TYPE:
                    console.log("Received WordWallCompleteEvent");

                    this.audioQueueService.hardReset();
                    this.wordWallRecordingService.deactivate();
                    this.eventBus.next(new ResetSelectionLoadedEvent());

                    // lets server know we are changing to the preview step
                    this.stepService.requestStep(new PreviewStep());
                    this.gaEventService.endDurationEvent("wordwall-duration", "wordwall", this._selectionName);
                    break;

                case PreviewStepEvent.TYPE:
                    console.log("Received event of type PreviewStepEvent");
                    // if we are already in the preview step don't start again.
                    if (this.isPreviewActiveClass === false) {
                        console.log("Initiating preview step after event.");
                        this.previewStep();
                    }
                    break;

                case ReadAndRecordStepEvent.TYPE:
                    this.readAndRecordStep();
                    break;

                case TaiEvent.TYPE:
                    this.closeGlossary();
                    break;

                case QuizStepEvent.TYPE:
                    this.quizStep();
                    break;

                case ReadSelectionEvent.TYPE:
                    this.audioQueueService.reset().subscribe(() => {
                        this.readSelection();
                    });
                    break;

                case ReadMyRecordingEvent.TYPE:
                    this.readMyRecording();
                    break;

                case RecordSelectionEvent.TYPE:
                    this.recordSelection();
                    break;

                case PauseSelectionEvent.TYPE:
                    this.pauseSelection();
                    break;

                case NextSentenceEvent.TYPE:
                    this.goFromSelectedSentence();
                    break;

                case CountdownStartEvent.TYPE:
                    this.showCountdown = true;
                    this.countDownDisplaying = true;
                    break;

                case PipelineSaveEvent.TYPE:
                    let pipelineEvent: PipelineSaveEvent = <PipelineSaveEvent>event;
                    if ((pipelineEvent.status === PIPELINE_API_STATUS.finish)
                        || (pipelineEvent.status === PIPELINE_API_STATUS.finishglossary)) {
                        // pipelineEvent.selection may not be set if we haven't saved anything within the selection
                        if (pipelineEvent.selection) {
                            this._selection = new CssSelection(pipelineEvent.selection);
                            this.contentCursor.init(this._selection);
                        }
                        this.previewStep();
                        this.fetchDataComplete();
                    }
                    break;
                case ActivityModalEvent.TYPE:
                    let activityModalEvent = <ActivityModalEvent>event;
                    if (activityModalEvent.status === ACTIVITY_STATUS.modal_close &&
                        activityModalEvent.data.type === Scilearn.FLUENCY_MODAL &&
                        activityModalEvent.data.automatic === true) {
                        this.setupTimerToPlayRecordingAudio();
                    }
                    break;
                default:
                    break;
            }
        });
        this.subscriptions.push(sub);

        this.eventBus.next(new StepStatusEvent(PreviewStepEvent.TYPE, 0));
        this.eventBus.next(new StepStatusEvent(ReadAndRecordStepEvent.TYPE, 0));

        this.fetchData(this._selectionName).subscribe(() => {
            this.fetchDataComplete();
        });
    }

    private fetchDataComplete(): void {
        if (this._selection.dictionary()) {
            this.recordingService.initPronunciationDictionary(this._selection.dictionary())
                .pipe(first()).subscribe(() => {
                this.log.log("fetchDataComplete: " + this._selection.metaData.referenceStory);
            });
        }

        this.glossaryService.injectEntries(this._selection.glossaryEntries);
        this.questionService.injectEntries(this._selection.questionEntries, this.isPipeline);
        this.wordWallRecordingService.injectEntries(this._selection.vocabularyEntries, this._selection.dictionary());

        // TODO: This setTimeout sucks, find better way to delay until after content view has stabilized
        setTimeout(() => {
            // TODO: think we can avoid the need for this using ngAfterViewInit, but needs some more work
            console.log("calling determineCurrentStep after timeout");
            this.stepStateUpdater.determineCurrentStep(this._selection, false); // will send out StepChange notices
            this.eventBus.next(new SelectionLoadedEvent(this._selection.metaData.referenceStory));

            // @ts-ignore
            if (imagesLoaded) {
                imagesLoaded(document.getElementsByClassName("reading-background"), () => {
                    if (!!this.contentCursor.selectedSentence && !!this.scrollService.getElement()) {
                        this.scrollService.scrollText();
                    }
                });
            } else {
                this.log.error("imagesLoaded is not defined, can't scroll");
            }

            this.eventBus.next(new PleaseWaitEvent({show: false, audio: false, destination: PleaseWaitTarget.READING}));
        }, 100);
    }

    private setActiveStep(type: BookSessionType): void {
        this.addOrSendTiming(type);
        this.isWordWallActiveClass = type === BookSessionType.WORDWALL;
        this.isPreviewActiveClass = type === BookSessionType.PREVIEW;
        this.isQuizActiveClass = type === BookSessionType.QUIZ;
        this.isReadAndRecordActiveClass = type === BookSessionType.FLUENCY;
    }

    private setCurrentReadingMode(mode:IReadingMode): void {
        this._currentReadingMode = mode;
        this.contentCursor.setAllowedSelectionType(this._currentReadingMode.allowedSelectionType);
        this._currentReadingMode.activate(this.isPipeline);
    }

    private fetchData(selectionName: string): Observable<any> {
        let contentObs: Observable<any> = Observable.create(observer => {
            this.contentService.retrieveSelection(selectionName, this.isPipeline)
                .subscribe(result => {
                    this._selection = new CssSelection(result);
                    // Override grade equiv for determining font theme with series average if needed
                    if (!!result.metaData.seriesName && !!this.assignmentService.currentSelectionGradeEquivFormat) {
                        this._selection.applyGradeLevelStyleClasses(this.assignmentService.currentSelectionGradeEquivFormat);
                    }
                    this.contentCursor.init(this._selection);
                    console.log("Completed fetching content-selection with name={} data={}", selectionName, result);
                    observer.next();
                    observer.complete();
                    if (this.isPipeline) {
                        this.previewStep();
                    }
                }, () => {
                    this.modalConfig.openMessageModal("selection_load_error");
                });
        });

        let userModelObs: Observable<any> = Observable.create(observer => {
            this.userModelService.retrieveUserModel(selectionName).subscribe(() => {
                console.log("Completed fetching of user model for content-selection=", selectionName);
                observer.next();
                observer.complete();
            });
        });

        return Observable.create((observer:Observer<any>) => {
            contentObs.subscribe(() => userModelObs.subscribe(() => {
                observer.next("");
                observer.complete();
            }));
        });
    }

    private wordWallStep(): void {
        this.setActiveStep(BookSessionType.WORDWALL);
        this.eventBus.next(new EnableExitButtonEvent());
    }

    private previewStep(): void {
        this.setActiveStep(BookSessionType.PREVIEW);
        this.initCursor();
        this.previewPausedMode();
        this.setWordColorLegendVisibility();
        this.eventBus.next(new EnableExitButtonEvent());
    }

    private readAndRecordStep(): void {
        this.setActiveStep(BookSessionType.FLUENCY);
        this.initCursor();
        this.readAndRecordPausedMode();
        this.setWordColorLegendVisibility();
        this.eventBus.next(new EnableExitButtonEvent());
    }

    private quizStep(): void {
        this.setActiveStep(BookSessionType.QUIZ);
        this.initCursor();
        this.quizPausedMode();
        this.setWordColorLegendVisibility();
        this.eventBus.next(new EnableExitButtonEvent());
    }

    private setWordColorLegendVisibility(): void {
        let isFluencyStep: boolean = this.stepService.isFluency;
        let hasFinishedRecording: boolean = this.selectionRecordingStatus.hasFinishedRecording();
        this.showWordColorLegend =  isFluencyStep && hasFinishedRecording;
    }

    private playPreviewAudioAsNecessary(): void {
        let noActivity = this.stepService.isNonActivity();
        let noPreviousProgress = this.userModelService.thinkAboutQuestionStates.length === 0;

        if (noActivity && noPreviousProgress && this.playPreviewInstruction) {
            this.playPreviewInstruction = false;
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new PleaseWaitEvent({show: false, audio: false,
                                                           destination: PleaseWaitTarget.READING}));
                this.eventBus.next(new AudioQueueEvent(
                    [
                        new AudioQueueItem("RA_STEP_1_04", TranslateAudio.YES),
                        new AudioQueueItem("RA_STEP_1_06", TranslateAudio.YES),
                        new AudioQueueItem("RA_STEP_1_05", TranslateAudio.YES)
                    ], InterruptAudio.YES));
            });
       }
    }

    private playFluencyAudioAsNecessary(): void {
        let noActivity = this.stepService.isNonActivity();
        let noPreviousProgress = this.stepStateUpdater.recordCountProgress() === 0;

        if (noActivity && noPreviousProgress && this.playFluencyInstruction) {
            this.playFluencyInstruction = false;
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new PleaseWaitEvent({show: false, audio: false,
                                                           destination: PleaseWaitTarget.READING}));
                this.eventBus.next(new AudioQueueEvent(
                    [new AudioQueueItem("RA_STEP_2_07", TranslateAudio.YES)], InterruptAudio.NO));
            });
        }
    }

    private playQuizAudioAsNecessary(): void {
        let noActivity = this.stepService.isNonActivity();
        let noPreviousProgress = this.userModelService.quizQuestionStates.length === 0;

        if (noActivity && noPreviousProgress && this.playQuizInstruction) {
            this.playQuizInstruction = false;
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new PleaseWaitEvent({show: false, audio: false,
                                                           destination: PleaseWaitTarget.READING}));
                this.eventBus.next(new AudioQueueEvent(
                    [new AudioQueueItem("RA_STEP_3_01", TranslateAudio.YES)], InterruptAudio.NO));
            });
        }
    }

    private previewPausedMode(): void {
        this.scrollService.enableScroll();
        this.stepService.resetCurrentStepActivity();
        this.setCurrentReadingMode(this.previewService);
        this.playPreviewAudioAsNecessary();
    }

    private readAndRecordPausedMode(): void {
        this.scrollService.enableScroll();
        this.stepService.resetCurrentStepActivity();
        this.setCurrentReadingMode(this.previewService);
        this.playFluencyAudioAsNecessary();
    }

    private quizPausedMode(): void {
        this.stepService.resetCurrentStepActivity();
        this.scrollService.enableScroll();
        this.setCurrentReadingMode(this.quizService);
        this.playQuizAudioAsNecessary();
    }

    public get isUIActive(): boolean {
        let countdownHidden = !this.showCountdown;
        let notScrolling = !this.scrollArrow;
        let showRedBorder = this._currentReadingMode ? this._currentReadingMode.changeUIWhenActive : false;
        return countdownHidden && notScrolling && showRedBorder;
    }

    public readSelection(): void {
        // Do we need to keep track of sentence progress??
        this._sentenceCount = 0;
        this.closeGlossary();
        this.stepService.setCurrentStepActivity(StepActivity.READING);
        this.setCurrentReadingMode(this.readToMeService);
        this.scrollService.disableScroll();
        this.ensureSelectedSentence();
        this.goFromSelectedSentence();
        this.readToMeService.startReadingActivity(Scilearn.__READTOME__);
        this.eventBus.next(new DisableExitButtonEvent());
    }

    public recordSelection(): void {
        // Do we need to keep track of sentence progress??
        this._sentenceCount = 0;
        this.scrollArrow = false;
        this.stepService.setCurrentStepActivity(StepActivity.RECORDING);
        this.setCurrentReadingMode(this.recordingService);
        this.scrollService.disableScroll();
        this.previewService.selectCurrentWord(); // need to set cursor to correct point for recording
        this.goFromRecordingSelectedSentence(true);
        this.eventBus.startActivityEvent(new ActivityEventData({timing: TimerStrategy.readRecord}));
        this.eventBus.next(new DisableExitButtonEvent());
    }

    public readMyRecording(): void {
        this._sentenceCount = 0;
        this.stepService.setCurrentStepActivity(StepActivity.READ_MY_RECORDING);
        this.setCurrentReadingMode(this.readMyRecordingService);
        this.scrollService.disableScroll();
        this.ensureSelectedSentence();
        this.goFromSelectedSentence();
        this.readToMeService.startReadingActivity(Scilearn.__PLAYBACK__);
        this.eventBus.next(new DisableExitButtonEvent());
    }

    public pauseSelection(): void {
        clearTimeout(this._sentenceDelayTimer);
        // TODO: still some edge cases here, like if we pause recording immediately after finishing the first sentence
        // of a new recording. TODO: also; do word colors and gray/black only show in step 2? what is current behavior?
        if (this.isPreviewActiveClass) {
            this.previewService.pause();
            this.previewPausedMode();
        } else if (this.isReadAndRecordActiveClass) {
            if (this.isRecording()) {
                this.recordingService.pause();
            }
            this.readAndRecordPausedMode();
        }

        this.stepService.resetCurrentStepActivity();
        this.readToMeService.endReadingActivity();
        // below is called in the recording.service pause and probably isn't relevant for other pauses. Nothing is
        // using this event currently anyway this.eventBus.next(new RecordingStatusEvent(RECORDING_STATUS.stop));
        this.eventBus.next(new EnableExitButtonEvent());
        this.scrollService.enableScroll();
    }

    public isScrollingDisabled(): boolean {
        return !this.scrollService.isScrollingAllowed();
    }

    private initiateScrollProcess(): void {
        let sentence = this.contentCursor.selectedSentence;
        this.scrollService.enableScroll();
        this.scrollService.scrollTextObservable.subscribe(() => {},
            () => {},
            () => {
                if (this.isRecording() && this.recordingService.isCountdownRun) {
                    this.playRecordingCue(() => { this._currentReadingMode.startSentence(
                        sentence,
                        this._currentReadingMode.sentenceFinishedCallback); });
                } else {
                    this._currentReadingMode.startSentence(
                        sentence,
                        this._currentReadingMode.sentenceFinishedCallback);
                }
                this.scrollService.disableScroll();
            });
        this.scrollService.scrollText();
    }

    private goFromSelectedSentence = () => {
        this.currentlyPlayingSentence = this.contentCursor.selectedSentence;
        this.log.log("goFromSelectedSentence, selectedSentence: " + this.contentCursor.selectedSentence);

        if (this.contentCursor.selectedSentence) {
            let inView: Boolean = this.scrollService.checkIfSentenceInView(this.contentCursor.selectedSentence);

            if (inView) {
                this.changeDetectorRef.detectChanges();
                this._currentReadingMode.startSentence(this.contentCursor.selectedSentence,
                                                       this._currentReadingMode.sentenceFinishedCallback);
            } else {
                this.initiateScrollProcess();
            }
        }
    };

    private goFromRecordingSelectedSentence = (initial?: boolean) => {
        this.currentlyPlayingSentence = this.contentCursor.selectedSentence;
        this.log.log("goFromRecordingSelectedSentence, selectedSentence: " + this.contentCursor.selectedSentence.id);

        if (this.contentCursor.selectedSentence) {
            let inView: Boolean = this.scrollService.checkIfSentenceInView(this.contentCursor.selectedSentence);

            if (inView) {
                this._currentReadingMode.startSentence(this.contentCursor.selectedSentence,
                    this._currentReadingMode.sentenceFinishedCallback);
            } else if (initial) {
                this.initiateScrollProcess();
            } else {
                // If arrow is visible, set first unrecorded sentence to gray so user doesn't think it should be read.
                ContentFormatter.setSentenceRecorded(this.contentCursor.selectedSentence, true,
                                                         this.contentCursor.getMode());
                this.scrollArrow = true;
            }
        }
    };

    private readingSentenceFinished = () => {
        if (!this.networkService.isOnline()) {
            this.contentCursor.goNextSentence();
            this.pauseSelection();
            this.audioQueueService.reset().subscribe(() => {
                this.modalConfig.openMessageModal("disconnect_network_error");
            });
            return;
        }

        this.contentCursor.goNextSentence();

        if (this.contentCursor.selectedSentence) {
            this._sentenceDelayTimer = window.setTimeout(this.goFromSelectedSentence, this._sentenceDelayTime);
        } else {
            this.log.log("readingSentenceFinished: no more sentences");
            // scroll to end to show any image at end of selection
            this.scrollService.scrollTextToEnd();
            // pause and reset the cursor to the first word of the first sentence
            this.initCursor();
            this.pauseSelection();
        }
    };

    private recordSentenceFinished = () => {
        this._ngZone.run(() => {
            if (this.contentCursor.selectedSentence) {
                this.goFromRecordingSelectedSentence();
            } else {
                this.log.log("recordSentenceFinished: no more sentences");
                // scroll to end to show any image at end of selection
                this.scrollService.scrollTextToEnd();
                // pause and reset the cursor to the first word of the first sentence
                this.initCursor();
                this.pauseSelection();
                this.contentCursor.startAtWord(this.contentCursor.firstWord);
                ContentFormatter.formatSelectionFromRecordReadingStates(this.contentCursor.selection,
                    this.userModelService.recordReadingStates);
                this.contentCursor.setMode(ContentCssClass.MODE_RECORDING_DONE);
            }
        });
    };

    private initCursor(): void {
        this.log.log("clearing cursor");
        this.contentCursor.clearSelection();
    }

    private ensureSelectedSentence(): void {
        if (!this.contentCursor.selectedSentence) {
            this.contentCursor.selectSentence(this.contentCursor.firstSentence);
        }
    }

    private isStepSwitchEvent(e:RaEvent): boolean {
        return [WordWallStepEvent.TYPE, PreviewStepEvent.TYPE, ReadAndRecordStepEvent.TYPE, QuizStepEvent.TYPE]
                .indexOf(e.type) !== -1;
    }

    private reactToStepSwitching(): void {
        this.eventBus.pipe(filter((e:RaEvent) => this.isStepSwitchEvent(e))).subscribe((e:RaEvent) => {
            this.glossaryService.setGlossaryOpened(false);
            this.showTai = false;
            this.showCountdown = false;
            this.countDownDisplaying = false;
            this.showWordColorLegend = false;
        });
    }

    public scrollAfterRecordArrowClick(): void {
        this.scrollArrow = false;
        ContentFormatter.setSentenceRecorded(this.contentCursor.selectedSentence, false,
                                             this.contentCursor.getMode());
        this.initiateScrollProcess();
    }

    public initSidePanelHeight(): {"height": string} {
        let height = (window.innerHeight / 2) - 150;
        if (window.innerHeight < 768 || height > 450) {
            height = 350;
        }
        return {"height": height + "px"};
    }

    public getDeviceHeight(): {"height": string, "overflow": string} {
        const height = window ? window.innerHeight : 0;
        return {
            "height": height + "px",
            "overflow": "hidden"
        };
    }

    public onResizeForSideCard($event): void {
        if ($event.target.innerWidth < 991) {
            this.sidePosFixed = false;
        }
    }

    public initReadingViewHeight(): {"height": string} {
        let height = window.innerHeight - 76;
        if (window.innerWidth < 992) {
            height = window.innerHeight - 155;
        }
        return {"height": height + "px"};
    }

    public addGridsHighlight(): boolean {
        // tslint:disable-next-line:no-string-literal
        return this.activatedRoute.snapshot.params["grids"] === "true";
    }

    public microphoneCheck(): void {
        this.eventBus.next(new MicrophoneCheckEvent(MICROPHONECHECK_STATUS.manual));
    }

    public closeGlossary(): void {
        if (this.glossaryService.isGlossaryOpened()) {
            this.eventBus.next(
                new ActivityEvent(ACTIVITY_STATUS.activity_end, new ActivityEventData({type: "glossary"}))
            );
        }
        this.glossaryService.setGlossaryOpened(false);
    }

    public taiSelected(event): void {
        this.audioQueueService.reset().subscribe(() => {
            this.closeGlossary();
            this.showTai = true;
            this.currentTaiQuestion = this.questionService.retrieveQuestion(event.id);
            this.setCurrentReadingMode(this.quizService);
            this.eventBus.startActivityEvent(new ActivityEventData({timing: TimerStrategy.question}));
            this.eventBus.next(new TaiEvent(TAI_STATUS.begin_tai, event.id));
            this.eventBus.next(new DisableExitButtonEvent());
        });
    }

    public closeTai(): void {
        this.showTai = false;
        this.currentTaiQuestion = undefined;
        this.setCurrentReadingMode(this.previewService);
        this.eventBus.endActivityEvent(new ActivityEventData({timing: TimerStrategy.question}));
        this.eventBus.next(new TaiEvent(TAI_STATUS.end_tai, ""));
        this.eventBus.next(new EnableExitButtonEvent());
        this.changeDetectorRef.detectChanges();
    }

    public closeCountdown(): void {
        // avoid duplicate calls to this function (maybe double click/double press issue) on iPad
        if (this.countDownDisplaying) {
            this.countDownDisplaying = false;
            this.log.log("calling closeCountdown");
            this.playRecordingCue(() => {
                this.log.log("sending countdownStopEvent");
                this.eventBus.next(new CountdownStopEvent());
                this.showCountdown = false;
            });
        }
    }

    public ngOnDestroy(): void {
        this.addOrSendTiming("destroy");
        super.ngOnDestroy();
        this.pointService.cleanup();
        this.recordingService.changeDetectorRef = null;
        this.scrollService.changeDetectorRef = null;
    }

    public isRecording(): boolean {
        return this.stepService.isRecording;
    }

    public getScrollArrowVisibility(): boolean {
        return this.scrollArrow && this.stepService.isRecording;
    }

    public isWordColorLegendVisible(): boolean {
        return this.showWordColorLegend && !this.stepService.isRecording && !this.showCountdown;
    }

    private playRecordingCue(callback: Function): void {
        this.eventBus.next(new AudioQueueEvent(
            [new AudioQueueItem(this.config.audioUrl + "SFX/RA_STEP_2_RECORD_CUE.mp3", TranslateAudio.NO, callback, callback)],
            InterruptAudio.YES));
    }

    private setupTimerToPlayRecordingAudio(): void {
        // sets a timer for 30 seconds
      const timerSource =  timer(30000);
        let timerObservable: Subscription = timerSource.subscribe(() => {
            if (this.stepService.isFluency) {
                this.eventBus.next(new AudioQueueEvent(
                    [new AudioQueueItem("RA_STEP_2_05", TranslateAudio.YES)], InterruptAudio.YES));
            } else {
                timerObservable.unsubscribe();
            }
        });

        // we just want to look at the first mousedown event
        let mouseDownObserver = fromEvent(document, "mousedown").pipe(take(1));
        mouseDownObserver.subscribe(() => {
            if (timerObservable) {
                timerObservable.unsubscribe();
            }
        });
    }

    private addOrSendTiming(eventName):void {

        if (!this.timingEvent.get(eventName)) {
            this.timingEvent.set(eventName,  Math.floor(Date.now()));
        }

        Array.from(this.timingEvent.keys(), (value: string) => {
            if (value !== eventName) {
                this.gaEventService.emitTiming("app", "step", value,
                    Math.floor(Date.now()) - this.timingEvent.get(value));
                this.timingEvent.delete(value);
            }
        });
    }
}
